// MaintenanceVendorRequestForm.jsx
// Form for maintenance vendors to request access

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Paper,
  Alert,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Business,
  Phone,
  Email,
  CalendarToday,
  UploadFile,
} from "@mui/icons-material";
import { getDatabase, ref, get } from "firebase/database";
import momentTz from "moment-timezone";

// Timezone mapping (same as email server and GenerateVendorLinkDialog)
const stateToTimezone = {
  'California': 'America/Los_Angeles', 'CA': 'America/Los_Angeles',
  'Nevada': 'America/Los_Angeles', 'NV': 'America/Los_Angeles',
  'Oregon': 'America/Los_Angeles', 'OR': 'America/Los_Angeles',
  'Washington': 'America/Los_Angeles', 'WA': 'America/Los_Angeles',
  'Arizona': 'America/Phoenix', 'AZ': 'America/Phoenix',
  'Colorado': 'America/Denver', 'CO': 'America/Denver',
  'Idaho': 'America/Denver', 'ID': 'America/Denver',
  'Montana': 'America/Denver', 'MT': 'America/Denver',
  'New Mexico': 'America/Denver', 'NM': 'America/Denver',
  'Utah': 'America/Denver', 'UT': 'America/Denver',
  'Wyoming': 'America/Denver', 'WY': 'America/Denver',
  'Alabama': 'America/Chicago', 'AL': 'America/Chicago',
  'Arkansas': 'America/Chicago', 'AR': 'America/Chicago',
  'Illinois': 'America/Chicago', 'IL': 'America/Chicago',
  'Indiana': 'America/Chicago', 'IN': 'America/Chicago',
  'Iowa': 'America/Chicago', 'IA': 'America/Chicago',
  'Kansas': 'America/Chicago', 'KS': 'America/Chicago',
  'Kentucky': 'America/Chicago', 'KY': 'America/Chicago',
  'Louisiana': 'America/Chicago', 'LA': 'America/Chicago',
  'Michigan': 'America/Chicago', 'MI': 'America/Chicago',
  'Minnesota': 'America/Chicago', 'MN': 'America/Chicago',
  'Missouri': 'America/Chicago', 'MO': 'America/Chicago',
  'Nebraska': 'America/Chicago', 'NE': 'America/Chicago',
  'North Dakota': 'America/Chicago', 'ND': 'America/Chicago',
  'Oklahoma': 'America/Chicago', 'OK': 'America/Chicago',
  'South Dakota': 'America/Chicago', 'SD': 'America/Chicago',
  'Tennessee': 'America/Chicago', 'TN': 'America/Chicago',
  'Texas': 'America/Chicago', 'TX': 'America/Chicago',
  'Wisconsin': 'America/Chicago', 'WI': 'America/Chicago',
  'Connecticut': 'America/New_York', 'CT': 'America/New_York',
  'Delaware': 'America/New_York', 'DE': 'America/New_York',
  'Florida': 'America/New_York', 'FL': 'America/New_York',
  'Georgia': 'America/New_York', 'GA': 'America/New_York',
  'Maine': 'America/New_York', 'ME': 'America/New_York',
  'Maryland': 'America/New_York', 'MD': 'America/New_York',
  'Massachusetts': 'America/New_York', 'MA': 'America/New_York',
  'New Hampshire': 'America/New_York', 'NH': 'America/New_York',
  'New Jersey': 'America/New_York', 'NJ': 'America/New_York',
  'New York': 'America/New_York', 'NY': 'America/New_York',
  'North Carolina': 'America/New_York', 'NC': 'America/New_York',
  'Ohio': 'America/New_York', 'OH': 'America/New_York',
  'Pennsylvania': 'America/New_York', 'PA': 'America/New_York',
  'Rhode Island': 'America/New_York', 'RI': 'America/New_York',
  'South Carolina': 'America/New_York', 'SC': 'America/New_York',
  'Vermont': 'America/New_York', 'VT': 'America/New_York',
  'Virginia': 'America/New_York', 'VA': 'America/New_York',
  'West Virginia': 'America/New_York', 'WV': 'America/New_York',
  'Alaska': 'America/Anchorage', 'AK': 'America/Anchorage',
  'Hawaii': 'America/Adak', 'HI': 'America/Adak',
};

const getTimezoneFromState = (state) => {
  if (!state) return 'America/New_York';
  return stateToTimezone[state] || 'America/New_York';
};

// Two-hour block start hours (e.g. 8 = 8:00 AM) in property local time
const TWO_HOUR_BLOCK_START_HOURS = [8, 10, 12, 14, 16]; // 8-10, 10-12, 12-2, 2-4, 4-6

const MaintenanceVendorRequestForm = ({
  listingId,
  propertyAddress,
  onSubmit,
  loadingSubmit,
  predefinedSlots = null, // If provided, vendor can only select from these slots
  linkId = null,
  vendorLink = null, // Link data (requirements, tenant details) to show on form
}) => {
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [selectedDateId, setSelectedDateId] = useState("");
  const [weeklyDate, setWeeklyDate] = useState("");
  const [idDocument, setIdDocument] = useState(null);
  const [idDocumentName, setIdDocumentName] = useState("");
  const [dateError, setDateError] = useState("");
  const [formError, setFormError] = useState("");
  const dateInputRef = useRef(null);

  const [weeklyTimes, setWeeklyTimes] = useState([]);
  const [specificDates, setSpecificDates] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(true);
  const [propertyState, setPropertyState] = useState(null);
  const hasProcessedSlots = useRef(false);

  // Vendor-side: date chosen by vendor for 2-hour arrival window (no owner slots)
  const [vendorSelectedDate, setVendorSelectedDate] = useState("");

  const useVendorTwoHourBlocks = !predefinedSlots || (Array.isArray(predefinedSlots) && predefinedSlots.length === 0);

  // Format availability helper - uses property timezone for display
  // Memoized to prevent re-creation on every render
  const formatAvailability = useCallback((slot, timezone = null) => {
    const tz = timezone || getTimezoneFromState(propertyState);

    if (slot.day) {
      // Weekly slot: day + time from ISO strings
      try {
        const startMoment = momentTz(slot.startTime).tz(tz);
        const endMoment = momentTz(slot.endTime).tz(tz);
        const startStr = startMoment.format('h:mm A');
        const endStr = endMoment.format('h:mm A');
        return `${slot.day} ${startStr} - ${endStr}`;
      } catch (e) {
        // Fallback if date parsing fails
        return `${slot.day} ${slot.startTime || ''} - ${slot.endTime || ''}`;
      }
    }

    // Specific date slot
    if (slot.startTime && slot.endTime) {
      try {
        const startMoment = momentTz(slot.startTime).tz(tz);
        const endMoment = momentTz(slot.endTime).tz(tz);
        const dateStr = startMoment.format('M/D/YYYY');
        const startStr = startMoment.format('h:mm A');
        const endStr = endMoment.format('h:mm A');
        return `${dateStr} ${startStr} - ${endStr}`;
      } catch (e) {
        // Fallback
        return slot.displayText || "Available";
      }
    }

    if (slot.start && slot.end) {
      try {
        const startMoment = momentTz(slot.start).tz(tz);
        const endMoment = momentTz(slot.end).tz(tz);
        const dateStr = startMoment.format('M/D/YYYY');
        const startStr = startMoment.format('h:mm A');
        const endStr = endMoment.format('h:mm A');
        return `${dateStr} ${startStr} - ${endStr}`;
      } catch (e) {
        return slot.displayText || "Available";
      }
    }

    if (slot.date && slot.startTime && slot.endTime) {
      try {
        const dateMoment = momentTz(slot.date).tz(tz);
        const startMoment = momentTz(slot.startTime).tz(tz);
        const endMoment = momentTz(slot.endTime).tz(tz);
        const dateStr = dateMoment.format('M/D/YYYY');
        const startStr = startMoment.format('h:mm A');
        const endStr = endMoment.format('h:mm A');
        return `${dateStr} ${startStr} - ${endStr}`;
      } catch (e) {
        return slot.displayText || "Available";
      }
    }

    return slot.displayText || "Available";
  }, [propertyState]);

  // Fetch property state separately to avoid re-rendering issues
  useEffect(() => {
    if (listingId && !propertyState) {
      const fetchPropertyState = async () => {
        try {
          const database = getDatabase();
          const propertyRef = ref(database, `listings/${listingId}`);
          const propertySnap = await get(propertyRef);

          if (propertySnap.exists()) {
            const propertyData = propertySnap.val();
            if (propertyData.state) {
              setPropertyState(propertyData.state);
            }
          }
        } catch (error) {
          console.error("Error fetching property state:", error);
        }
      };
      fetchPropertyState();
    }
    // Only run when listingId changes or propertyState is null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  // When vendor chooses their own date: generate 2-hour arrival blocks for that date (vendor-side only)
  useEffect(() => {
    if (!useVendorTwoHourBlocks || !vendorSelectedDate) {
      if (useVendorTwoHourBlocks) setSpecificDates([]);
      return;
    }
    const tz = propertyState ? getTimezoneFromState(propertyState) : "America/New_York";
    const today = momentTz().tz(tz);
    const selectedMoment = momentTz.tz(vendorSelectedDate, "YYYY-MM-DD", tz);
    const isToday = selectedMoment.isSame(today, "day");
    const blocks = [];
    TWO_HOUR_BLOCK_START_HOURS.forEach((startHour) => {
      const start = selectedMoment.clone().hour(startHour).minute(0).second(0).millisecond(0);
      const end = start.clone().add(2, "hours");
      if (isToday && end.isSameOrBefore(today)) return; // skip past blocks
      const id = `block-${vendorSelectedDate}-${startHour}`;
      const displayText = `${start.format("M/D/YYYY")} ${start.format("h:mm A")} - ${end.format("h:mm A")}`;
      blocks.push({
        id,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        displayText,
      });
    });
    setSpecificDates(blocks);
    setSelectedDateId(""); // reset so vendor must pick a block
  }, [useVendorTwoHourBlocks, vendorSelectedDate, propertyState]);

  // Fetch available times for the property (public access - no auth required)
  // OR use predefined slots from vendor link. When no slots from owner, time is handled on vendor side (2-hour blocks).
  useEffect(() => {
    if (!listingId) return;

    // Prevent re-processing if we've already processed the slots
    if (hasProcessedSlots.current) {
      return;
    }

    const fetchTimes = async () => {
      try {
        setLoadingTimes(true);

        // If predefined slots are provided (from vendor link), use those
        if (predefinedSlots && Array.isArray(predefinedSlots) && predefinedSlots.length > 0) {
          hasProcessedSlots.current = true;

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const weekly = [];
          const specific = [];

          // Use a stable timezone for formatting (use propertyState if available, otherwise default)
          // Wait a bit for propertyState to be set if it's being fetched
          const tz = propertyState ? getTimezoneFromState(propertyState) : 'America/New_York';

          predefinedSlots.forEach((slot) => {
            if (slot.day) {
              // Weekly availability
              weekly.push({
                id: slot.id || `weekly-${slot.day}-${slot.startTime}`,
                day: slot.day,
                startTime: slot.startTime,
                endTime: slot.endTime,
                displayText: slot.displayText || formatAvailability(slot, tz),
              });
            } else {
              // Specific date availability
              const startDate = slot.startTime ? new Date(slot.startTime) : (slot.start ? new Date(slot.start) : null);
              if (startDate) {
                startDate.setHours(0, 0, 0, 0);
                if (startDate.getTime() >= today.getTime()) {
                  specific.push({
                    id: slot.id || `specific-${slot.startTime || slot.start}`,
                    startTime: slot.startTime || slot.start,
                    endTime: slot.endTime || slot.end,
                    date: slot.date,
                    displayText: slot.displayText || formatAvailability(slot, tz),
                  });
                }
              }
            }
          });

          setWeeklyTimes(weekly);
          setSpecificDates(specific);

          // Auto-select the first (and only) predefined slot
          if (weekly.length > 0) {
            setSelectedDateId(weekly[0].id);
          } else if (specific.length > 0) {
            setSelectedDateId(specific[0].id);
          }

          setLoadingTimes(false);
          return;
        }

        // Otherwise, fetch from calendar (legacy behavior)
        hasProcessedSlots.current = true;
        const database = getDatabase();

        // Find property owner
        const propertyRef = ref(database, `listings/${listingId}`);
        const propertySnap = await get(propertyRef);

        if (!propertySnap.exists()) {
          setFormError("Property not found.");
          setLoadingTimes(false);
          return;
        }

        const propertyData = propertySnap.val();
        // Store property state for timezone calculation (only if not already set)
        if (propertyData.state && !propertyState) {
          setPropertyState(propertyData.state);
        }
        const ownerId = propertyData.ownerId || propertyData.ownerUid;

        if (!ownerId) {
          setFormError("Property owner information not available.");
          setLoadingTimes(false);
          return;
        }

        // Fetch availability from owner's properties
        const availabilityRef = ref(database, `users/${ownerId}/properties/${listingId}/availability`);
        const availabilitySnap = await get(availabilityRef);

        if (!availabilitySnap.exists()) {
          setWeeklyTimes([]);
          setSpecificDates([]);
          setLoadingTimes(false);
          return;
        }

        const availabilities = availabilitySnap.val() || [];

        // Filter out maintenance-only slots and parse into weekly and specific dates
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const weekly = [];
        const specific = [];

        availabilities.forEach((slot) => {
          // Skip maintenance-only slots (these are for maintenance vendors)
          if (slot.isMaintenance) return;

          if (slot.day) {
            // Weekly availability
            weekly.push({
              id: slot.id || `weekly-${slot.day}-${slot.startTime}`,
              day: slot.day,
              startTime: slot.startTime,
              endTime: slot.endTime,
              displayText: formatAvailability(slot),
            });
          } else if (slot.startTime && slot.endTime && !slot.day) {
            // Specific date availability with startTime/endTime (ISO strings)
            const startDate = new Date(slot.startTime);
            startDate.setHours(0, 0, 0, 0);
            if (startDate.getTime() >= today.getTime()) {
              specific.push({
                id: slot.id || `specific-${slot.startTime}`,
                startTime: slot.startTime,
                endTime: slot.endTime,
                displayText: formatAvailability(slot),
              });
            }
          } else if (slot.start && slot.end) {
            // Specific date availability - only show future dates
            const startDate = new Date(slot.start);
            startDate.setHours(0, 0, 0, 0);
            if (startDate.getTime() >= today.getTime()) {
              specific.push({
                id: slot.id || `specific-${slot.start}`,
                startTime: slot.start,
                endTime: slot.end,
                displayText: formatAvailability(slot),
              });
            }
          } else if (slot.date) {
            // Legacy format with date field
            const slotDate = new Date(slot.date);
            slotDate.setHours(0, 0, 0, 0);
            if (slotDate.getTime() >= today.getTime()) {
              specific.push({
                id: slot.id || `specific-${slot.date}`,
                startTime: slot.startTime,
                endTime: slot.endTime,
                date: slot.date,
                displayText: formatAvailability(slot),
              });
            }
          }
        });

        setWeeklyTimes(weekly);
        setSpecificDates(specific);
      } catch (error) {
        console.error("Error fetching available times:", error);
        setFormError("Failed to load available time slots. Please try again.");
      } finally {
        setLoadingTimes(false);
      }
    };

    fetchTimes();
    // Only depend on listingId and predefinedSlots, not propertyState or formatAvailability to avoid re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, predefinedSlots]);

  // Reset the processed flag when predefinedSlots or listingId change
  useEffect(() => {
    hasProcessedSlots.current = false;
  }, [predefinedSlots, listingId]);

  const selectedWeeklyTime = weeklyTimes.find((t) => t.id === selectedDateId);
  const isWeekly = !!selectedWeeklyTime;

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setWeeklyDate(newDate);

    if (isWeekly && newDate) {
      const dayOfWeek = new Date(newDate).toLocaleDateString("en-US", {
        weekday: "long",
      });
      setDateError(
        dayOfWeek === selectedWeeklyTime.day
          ? ""
          : `Please select a ${selectedWeeklyTime.day}`,
      );
    } else {
      setDateError(newDate ? "" : "Please select a date");
    }
  };

  const openCalendar = () => {
    if (dateInputRef.current?.showPicker) {
      dateInputRef.current.showPicker();
    } else {
      dateInputRef.current?.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (PDF, JPG, PNG)
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setFormError("Please upload a PDF, JPG, or PNG file for ID document.");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFormError("ID document must be less than 5MB.");
        return;
      }
      setIdDocument(file);
      setIdDocumentName(file.name);
      setFormError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Validation
    if (!companyName.trim()) {
      setFormError("Company name is required.");
      return;
    }
    if (!phone.trim()) {
      setFormError("Phone number is required.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setFormError("Valid email address is required.");
      return;
    }
    if (!selectedDateId) {
      setFormError("Please select an available time slot.");
      return;
    }
    // For vendor-side 2-hour blocks, require date and block selection
    if (useVendorTwoHourBlocks) {
      if (!vendorSelectedDate) {
        setFormError("Please select a date for your visit.");
        return;
      }
    }
    // For predefined slots, skip weekly date validation as it's already set
    else if (!predefinedSlots) {
      if (isWeekly && !weeklyDate) {
        setFormError("Please select a date for the weekly availability.");
        return;
      }
      if (isWeekly && dateError) {
        setFormError(dateError);
        return;
      }
    }
    if (!idDocument) {
      setFormError("Please upload a copy of your ID.");
      return;
    }

    // Build date details
    const selected = [...weeklyTimes, ...specificDates].find((t) => t.id === selectedDateId);
    if (!selected) {
      setFormError("Invalid availability selected.");
      return;
    }

    const data = {
      companyName: companyName.trim(),
      phone: phone.trim(),
      email: email.trim(),
      linkId: linkId || null,
    };

    if (selected.day) {
      // For predefined weekly slots, use the slot's start/end time directly
      if (predefinedSlots) {
        // Use the slot's startTime/endTime which should already be a specific date
        data.dateDetails = {
          type: "Weekly",
          displayText: selected.displayText,
          startTime: selected.startTime,
          endTime: selected.endTime,
        };
      } else {
        // For non-predefined weekly slots, require date selection
        if (!weeklyDate) {
          setFormError("Select a date for the weekly slot");
          return;
        }
        const startDT = new Date(weeklyDate);
        startDT.setHours(
          new Date(selected.startTime).getHours(),
          new Date(selected.startTime).getMinutes()
        );
        const endDT = new Date(weeklyDate);
        endDT.setHours(
          new Date(selected.endTime).getHours(),
          new Date(selected.endTime).getMinutes()
        );
        data.dateDetails = {
          type: "Weekly",
          displayText: `${formatAvailability(selected)} on ${weeklyDate}`,
          startTime: startDT.toISOString(),
          endTime: endDT.toISOString(),
        };
      }
    } else {
      data.dateDetails = {
        type: "Specific Date",
        displayText: selected.displayText,
        startTime: selected.startTime,
        endTime: selected.endTime,
      };
    }

    // 24-hour notice: if link requires it, flag scheduling earlier than 24 hours from now
    if (vendorLink?.linkRequirements?.twentyFourHourNotice && data.dateDetails?.startTime) {
      const slotStart = new Date(data.dateDetails.startTime).getTime();
      const minStart = Date.now() + 24 * 60 * 60 * 1000;
      if (slotStart < minStart) {
        setFormError(
          "This property requires at least 24 hours notice for non-emergency visits. Please select a later date or arrival window."
        );
        return;
      }
    }

    // Submit with ID document
    await onSubmit(data, idDocument);
  };

  const isSubmitDisabled =
    loadingSubmit ||
    loadingTimes ||
    (useVendorTwoHourBlocks ? !vendorSelectedDate || !selectedDateId : isWeekly && (dateError || !weeklyDate));

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 600,
        mx: "auto",
        mt: { xs: 2, sm: 4 },
        mb: 4,
      }}
    >
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Maintenance Vendor Access Request
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {propertyAddress || "Property Access Request"}
      </Typography>

      {/* Link requirements & tenant details (same as shown in request details) */}
      {vendorLink && (vendorLink.linkRequirements || vendorLink.tenantDetails) && (
        <Box sx={{ mb: 3, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          {vendorLink.linkRequirements && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Link Requirements
              </Typography>
              {vendorLink.linkRequirements.twentyFourHourNotice && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  • 24 Hour Notice Minimum (Non-Emergency Tenant Occupied)
                </Typography>
              )}
              {vendorLink.linkRequirements.petsOnProperty && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  • Pets on Property
                </Typography>
              )}
              {vendorLink.linkRequirements.emergency && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  • Emergency: {vendorLink.linkRequirements.emergencyType || "Emergency maintenance"}
                </Typography>
              )}
            </>
          )}
          {vendorLink.linkRequirements && vendorLink.tenantDetails && <Divider sx={{ my: 2 }} />}
          {vendorLink.tenantDetails && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Tenant & Property Details
              </Typography>
              {vendorLink.tenantDetails.tenantFirstName && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  <strong>Tenant Name:</strong> {vendorLink.tenantDetails.tenantFirstName}
                </Typography>
              )}
              {vendorLink.tenantDetails.tenantPhone && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  <strong>Tenant Phone:</strong> {vendorLink.tenantDetails.tenantPhone}
                </Typography>
              )}
              {vendorLink.tenantDetails.tenantEmail && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  <strong>Tenant Email:</strong> {vendorLink.tenantDetails.tenantEmail}
                </Typography>
              )}
              {vendorLink.tenantDetails.lockLocation && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  <strong>Lock Location:</strong> {vendorLink.tenantDetails.lockLocation}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {formError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError("")}>
          {formError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* Company Name */}
        <TextField
          fullWidth
          label="Company Name"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          required
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Business />
              </InputAdornment>
            ),
          }}
        />

        {/* Phone Number */}
        <TextField
          fullWidth
          label="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          margin="normal"
          type="tel"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone />
              </InputAdornment>
            ),
          }}
        />

        {/* Email */}
        <TextField
          fullWidth
          label="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          margin="normal"
          type="email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            ),
          }}
        />

        {/* ID Document Upload */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Upload ID Document (Required)
          </Typography>
          <input
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: "none" }}
            id="id-document-upload"
            type="file"
            onChange={handleFileChange}
            required
          />
          <label htmlFor="id-document-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFile />}
              fullWidth
              sx={{ mb: 1 }}
            >
              {idDocumentName || "Choose ID Document (PDF, JPG, or PNG)"}
            </Button>
          </label>
          {idDocumentName && (
            <Typography variant="caption" color="text.secondary">
              Selected: {idDocumentName}
            </Typography>
          )}
        </Box>

        {/* Time Slot Selection */}
        <Typography variant="body1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
          {predefinedSlots?.length ? "Scheduled Time Slot" : useVendorTwoHourBlocks ? "Select Date & Arrival Window" : "Select Available Time Slot"}
        </Typography>
        {predefinedSlots?.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The property owner has pre-selected this time slot for your maintenance visit. This cannot be changed.
          </Typography>
        ) : useVendorTwoHourBlocks ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose a date and a 2-hour arrival window for your visit (e.g. 11 AM - 1 PM).
            </Typography>
            {vendorLink?.linkRequirements?.twentyFourHourNotice && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                This property requires at least 24 hours notice. Please schedule your visit at least 24 hours from now.
              </Alert>
            )}
          </>
        ) : null}

        {loadingTimes ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {useVendorTwoHourBlocks ? (
              /* Vendor-side: date picker then 2-hour block dropdown */
              <>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Date for visit
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={vendorSelectedDate}
                    onChange={(e) => {
                      setVendorSelectedDate(e.target.value);
                      setSelectedDateId("");
                    }}
                    inputProps={{
                      min: momentTz().format("YYYY-MM-DD"),
                      max: momentTz().add(30, "days").format("YYYY-MM-DD"),
                    }}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />
                </Box>
                {vendorSelectedDate && (
                 <FormControl fullWidth required margin="normal">
                 <InputLabel
                   id="arrival-window-label"
                   shrink={true}   // 🔥 KEY FIX
                 >
                   Arrival window
                 </InputLabel>
               
                 <Select
                   labelId="arrival-window-label"
                   id="arrival-window-select"
                   value={selectedDateId}
                   label="Arrival window"
                   onChange={(e) => setSelectedDateId(e.target.value)}
                   displayEmpty
                   renderValue={(v) =>
                     v
                       ? specificDates.find((t) => t.id === v)?.displayText ?? ""
                       : "Select a 2-hour block"
                   }
                 >
                   <MenuItem value="" disabled>
                     Select a 2-hour block
                   </MenuItem>
               
                   {specificDates.map((t) => (
                     <MenuItem key={t.id} value={t.id}>
                       {t.displayText}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
               
                )}
              </>
            ) : predefinedSlots?.length ? (
              // Show pre-selected slot as read-only
              <TextField
                fullWidth
                value={
                  selectedDateId
                    ? (() => {
                      const allSlots = [...weeklyTimes, ...specificDates];
                      const selected = allSlots.find(s => s.id === selectedDateId);
                      return selected ? selected.displayText : "Selected slot";
                    })()
                    : "No slot selected"
                }
                InputProps={{
                  readOnly: true,
                }}
                margin="normal"
                sx={{
                  '& .MuiInputBase-input': {
                    bgcolor: 'action.disabledBackground',
                    cursor: 'not-allowed',
                  },
                }}
              />
            ) : (
              // Allow selection from calendar availability (legacy)
              <TextField
                fullWidth
                select
                value={selectedDateId}
                onChange={(e) => {
                  setSelectedDateId(e.target.value);
                  setWeeklyDate("");
                  setDateError("");
                }}
                required
                margin="normal"
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select a time slot</option>
                {weeklyTimes.length > 0 && (
                  <optgroup label="Weekly Availability">
                    {weeklyTimes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {formatAvailability(t)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {specificDates.length > 0 && (
                  <optgroup label="Specific Dates">
                    {specificDates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.displayText}
                      </option>
                    ))}
                  </optgroup>
                )}
              </TextField>
            )}

            {/* Weekly Date Picker - Only show if not predefined slots and not vendor 2-hour blocks */}
            {isWeekly && !predefinedSlots?.length && !useVendorTwoHourBlocks && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1.5, mb: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CalendarToday />}
                    onClick={openCalendar}
                    sx={{ mr: 2 }}
                  >
                    Select Date
                  </Button>
                  <input
                    type="date"
                    ref={dateInputRef}
                    value={weeklyDate}
                    onChange={handleDateChange}
                    style={{
                      visibility: "hidden",
                      position: "absolute",
                      width: 0,
                      height: 0,
                    }}
                    required
                    tabIndex={-1}
                  />
                  {weeklyDate ? (
                    <Typography variant="body1">
                      {new Date(weeklyDate).toLocaleDateString(undefined, {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No date selected
                    </Typography>
                  )}
                </Box>
                {selectedWeeklyTime && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Please select a {selectedWeeklyTime.day}
                  </Typography>
                )}
                {dateError && (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {dateError}
                  </Typography>
                )}
              </>
            )}
            {/* Show read-only date info for predefined weekly slots */}
            {isWeekly && predefinedSlots?.length && selectedWeeklyTime && (
              <Alert severity="info" sx={{ mt: 1 }}>
                This is a weekly recurring slot. Please contact the property owner to confirm the specific date for your visit.
              </Alert>
            )}
          </>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitDisabled}
          sx={{
            mt: 3,
            py: 1.5,
            backgroundColor: "#81c241",
            "&:hover": { backgroundColor: "#6aa832" },
          }}
        >
          {loadingSubmit ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Submit Access Request"
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default MaintenanceVendorRequestForm;

